import { useEffect, useState } from 'react'
import { fetchRulesAdmin, createRuleAdmin, deleteRuleAdmin } from '../../api/ws'
import type { Rule } from '../../store/types'

// Debug CRUD surface for the rules table. Every row is one IAM
// rule across every shape — position-tool, agent-resource,
// secret.use, host:approve_permission, etc. The typed surfaces
// (GrantsSegment, ResourcesSegment, secrets sheet, etc.) each own a
// constrained slice; this sheet is the unconstrained debug peek.
//
// See memory/project_rules_holistic_editor_todo.md for the
// holistic-rule-type catalogue that should eventually replace
// most direct use of this sheet.
//
// Reachable via gear → About → Debug → "Rules".
export function RulesSheet() {
  const [rules, setRules] = useState<Rule[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create form state — flat mirror of the rules table columns.
  // Free text; the operator is on their own to type valid values
  // (see TODO note re: typed editors).
  const [form, setForm] = useState({
    principal_scope_kind: 'position',
    principal_scope_value: '',
    resource_kind: 'tool',
    resource_id: '',
    action: '',
    predicate: '',
    decision: 'allow',
    expires_at: '',
  })

  async function refresh() {
    setBusy(true)
    try {
      const resp = await fetchRulesAdmin()
      if (resp.error) setError(resp.error)
      else setError(null)
      setRules(resp.rules ?? [])
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const resp = await createRuleAdmin({
        principalScopeKind: form.principal_scope_kind.trim(),
        principalScopeValue: form.principal_scope_value.trim(),
        resourceKind: form.resource_kind.trim(),
        resourceID: form.resource_id.trim() || null,
        action: form.action.trim(),
        predicate: form.predicate.trim() || null,
        decision: form.decision,
        expiresAt: form.expires_at.trim() || null,
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      setError(null)
      setForm((f) => ({ ...f, action: '', predicate: '', resource_id: '' }))
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function onDelete(id: string) {
    if (!confirm(`Delete rule #${id}?`)) return
    setBusy(true)
    try {
      const resp = await deleteRuleAdmin(id)
      if (resp.error) {
        setError(resp.error)
        return
      }
      setError(null)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        Generic CRUD over the <code>rules</code> table. Each row is
        one IAM rule across every shape — position-tool, agent-resource,
        secret.use, host:approve_permission, etc. Valid field values
        are not enforced here; typed editors will land per shape.
      </p>

      {error && (
        <div
          style={{
            background: '#fff0f0',
            border: '1px solid #f5a5a5',
            color: '#900',
            padding: 8,
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <form
        onSubmit={submitCreate}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 6,
          marginBottom: 16,
          padding: 8,
          background: '#f7f7f9',
          borderRadius: 4,
        }}
      >
        <Field label="principal_scope_kind">
          <input
            value={form.principal_scope_kind}
            onChange={(e) =>
              setForm((f) => ({ ...f, principal_scope_kind: e.target.value }))
            }
            placeholder="position | agent | human | organization"
          />
        </Field>
        <Field label="principal_scope_value">
          <input
            value={form.principal_scope_value}
            onChange={(e) =>
              setForm((f) => ({ ...f, principal_scope_value: e.target.value }))
            }
            placeholder="id of position/agent/human/org"
          />
        </Field>
        <Field label="resource_kind">
          <input
            value={form.resource_kind}
            onChange={(e) =>
              setForm((f) => ({ ...f, resource_kind: e.target.value }))
            }
            placeholder="tool | external_resource | secret | host | ..."
          />
        </Field>
        <Field label="resource_id">
          <input
            value={form.resource_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, resource_id: e.target.value }))
            }
            placeholder="(optional) id; NULL = any"
          />
        </Field>
        <Field label="action">
          <input
            value={form.action}
            onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}
            placeholder="Bash | external_resource.echo.echo | secret.use | ..."
          />
        </Field>
        <Field label="predicate">
          <input
            value={form.predicate}
            onChange={(e) =>
              setForm((f) => ({ ...f, predicate: e.target.value }))
            }
            placeholder="(optional) argv glob etc."
          />
        </Field>
        <Field label="decision">
          <select
            value={form.decision}
            onChange={(e) =>
              setForm((f) => ({ ...f, decision: e.target.value }))
            }
          >
            <option value="allow">allow</option>
            <option value="ask">ask</option>
            <option value="deny">deny</option>
          </select>
        </Field>
        <Field label="expires_at">
          <input
            value={form.expires_at}
            onChange={(e) =>
              setForm((f) => ({ ...f, expires_at: e.target.value }))
            }
            placeholder="(optional) RFC3339 e.g. 2026-12-31T23:59:59Z"
          />
        </Field>
        <button
          type="submit"
          disabled={busy}
          style={{
            gridColumn: '1 / span 2',
            padding: '8px 12px',
            borderRadius: 4,
            border: '1px solid #0a84ff',
            background: '#0a84ff',
            color: 'white',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? '…' : 'Create rule'}
        </button>
      </form>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <strong>{rules.length} rule{rules.length === 1 ? '' : 's'}</strong>
        <button onClick={() => void refresh()} disabled={busy}>
          Refresh
        </button>
      </div>

      {rules.length === 0 ? (
        <p style={{ opacity: 0.6 }}>(no rules in this organization)</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {rules.map((r) => (
            <RuleRow key={r.id} rule={r} onDelete={() => onDelete(r.id)} />
          ))}
        </ul>
      )}
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ opacity: 0.7, fontFamily: 'monospace', fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}

function RuleRow({ rule, onDelete }: { rule: Rule; onDelete: () => void }) {
  return (
    <li
      style={{
        padding: '10px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
          <span style={{ opacity: 0.5 }}>#{rule.id}</span>{' '}
          <strong>{rule.decision}</strong>{' '}
          <span>{rule.principal_scope_kind}:{rule.principal_scope_value}</span>{' '}
          → <span>{rule.resource_kind}{rule.resource_id ? `:${rule.resource_id}` : ''}</span>{' '}
          / <span>{rule.action}</span>
          {rule.predicate ? <> [{rule.predicate}]</> : null}
        </div>
        <div style={{ opacity: 0.5, fontSize: 11, marginTop: 2 }}>
          created {rule.created_at}
          {rule.expires_at ? ` · expires ${rule.expires_at}` : ''}
        </div>
      </div>
      <button
        onClick={onDelete}
        style={{
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid #ddd',
          background: 'white',
          color: '#900',
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        Delete
      </button>
    </li>
  )
}
